import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import toast from 'react-hot-toast'
import { 
  BeakerIcon, 
  ChartBarIcon, 
  UserGroupIcon,
  HeartIcon,
  ArchiveBoxIcon,
  CogIcon,
  BoltIcon,
  GlobeAltIcon
} from '@heroicons/react/24/outline'

const Login = () => {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const { login } = useAuth()
  const navigate = useNavigate()

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsLoading(true)

    try {
      await login(email, password)
      toast.success('Login successful!')
      navigate('/')
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Login failed')
    } finally {
      setIsLoading(false)
    }
  }

  const handleDemoLogin = async () => {
    setIsLoading(true)

    try {
      // Set demo user directly without API call
      const demoUser = {
        id: 1,
        email: 'demo@reprotech.com',
        username: 'demo',
        role: 'admin'
      }
      
      // Set token and navigate
      localStorage.setItem('token', 'demo-token-12345')
      toast.success('Demo access granted!')
      navigate('/')
    } catch (error: any) {
      toast.error('Demo access failed')
    } finally {
      setIsLoading(false)
    }
  }

  const platformFeatures = [
    { icon: UserGroupIcon, name: 'Animal Management', count: '8 modules' },
    { icon: BeakerIcon, name: 'Laboratory & Genomics', count: '10 modules' },
    { icon: HeartIcon, name: 'Veterinary Services', count: '5 modules' },
    { icon: ChartBarIcon, name: 'Farm Operations', count: '9 modules' },
    { icon: ArchiveBoxIcon, name: 'Business Management', count: '4 modules' },
    { icon: BoltIcon, name: 'AI & Analytics', count: '3 modules' },
    { icon: CogIcon, name: 'System Management', count: '6 modules' },
    { icon: GlobeAltIcon, name: 'Integration Hub', count: '2 modules' }
  ]

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="flex">
        {/* Left side - Platform showcase */}
        <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-800 relative overflow-hidden">
          <div className="absolute inset-0 bg-black opacity-10"></div>
          <div className="relative z-10 flex flex-col justify-center px-8 py-12">
            <div className="max-w-md">
              <h1 className="text-4xl font-bold text-white mb-4">
                Reprotech Platform
              </h1>
              <p className="text-xl text-blue-100 mb-8">
                Comprehensive Veterinary Biotechnology System with 45+ Specialized Modules
              </p>
              
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-white mb-4">Platform Features:</h3>
                <div className="grid grid-cols-1 gap-3">
                  {platformFeatures.map((feature, index) => (
                    <div key={index} className="flex items-center space-x-3 text-blue-100">
                      <feature.icon className="h-5 w-5 text-blue-200" />
                      <span className="text-sm">{feature.name}</span>
                      <span className="text-xs bg-blue-500 bg-opacity-30 px-2 py-1 rounded-full">
                        {feature.count}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="mt-8 p-4 bg-blue-500 bg-opacity-20 rounded-lg">
                <h4 className="text-white font-medium mb-2">Advanced Capabilities:</h4>
                <ul className="text-sm text-blue-100 space-y-1">
                  <li>• Genomic prediction models (GBLUP, GWAS)</li>
                  <li>• AI-powered breeding optimization</li>
                  <li>• Comprehensive biobank management</li>
                  <li>• Real-time monitoring & analytics</li>
                  <li>• Enterprise-level workflow automation</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Right side - Login form */}
        <div className="w-full lg:w-1/2 flex items-center justify-center px-8 py-12">
          <div className="max-w-md w-full space-y-8">
            <div>
              <div className="flex items-center justify-center mb-6">
                <HeartIcon className="h-12 w-12 text-blue-600 mr-3" />
                <div>
                  <h2 className="text-3xl font-extrabold text-gray-900">Reprotech</h2>
                  <p className="text-sm text-gray-600">Veterinary Biotechnology Platform</p>
                </div>
              </div>
              <h2 className="text-center text-2xl font-bold text-gray-900">
                Sign in to your account
              </h2>
              <p className="mt-2 text-center text-sm text-gray-600">
                Access 45+ specialized modules for livestock management
              </p>
            </div>

            <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
              <div className="space-y-4">
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                    Email address
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    required
                    className="mt-1 appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                    placeholder="Enter your email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                </div>
                <div>
                  <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                    Password
                  </label>
                  <input
                    id="password"
                    name="password"
                    type="password"
                    autoComplete="current-password"
                    required
                    className="mt-1 appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                </div>
              </div>

              <div>
                <button
                  type="submit"
                  disabled={isLoading}
                  className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? 'Signing in...' : 'Sign in'}
                </button>
              </div>

              <div className="mt-6">
                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-gray-300" />
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="px-2 bg-gray-50 text-gray-500">Or try the demo</span>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={handleDemoLogin}
                  disabled={isLoading}
                  className="mt-3 w-full flex justify-center py-2 px-4 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? 'Loading...' : 'Demo Access - Explore All 45+ Modules'}
                </button>
              </div>
            </form>

            <div className="mt-6 text-center text-xs text-gray-500">
              <p>Enterprise-level veterinary biotechnology platform</p>
              <p>with genomics, AI analytics, and comprehensive livestock management</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Login
